import { useMemo } from 'react'
import { TURNSTILE_EVENTS_CONFIG } from '../consts'

/**
 * Custom React hook that dynamically generates a collection of event handler functions.
 * These handlers are based on the configurations specified in TURNSTILE_EVENTS_CONFIG.
 * Each handler, when invoked, executes the provided `handleTurnstileStatus` function with
 * arguments tailored to the specific event type it corresponds to.
 *
 * @param {Function} handleTurnstileStatus - The primary callback function that processes
 * different turnstile events. This function is expected to handle various statuses and
 * conditions related to the turnstile's operation.
 * @returns {{[key: string]: Function}} An object mapping event types to their respective
 * handler functions. These handlers facilitate the invocation of `handleTurnstileStatus`
 * with the correct parameters for each event scenario.
 */
export const useTurnstileEvents = (handleTurnstileStatus) => {
  return useMemo(() => {
    // Transforms the TURNSTILE_EVENTS_CONFIG object into an accumulator object
    // where each key-value pair corresponds to an event type and its associated handler.
    return Object.entries(TURNSTILE_EVENTS_CONFIG).reduce(
      (accumulator, [eventType, eventArgs]) => {
        // Assigns a function to each event type that, when called, invokes
        // `handleTurnstileStatus` with the specific arguments relevant to that event.
        accumulator[eventType] = (token, methods) => {
          return handleTurnstileStatus({
            token,
            methods,
            args: eventArgs,
          })
        }
        return accumulator
      },
      {}, // Initial empty object
    )
  }, [handleTurnstileStatus]) // Dependency array to ensure re-computation only when handleTurnstileStatus changes.
}
